import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
// RxJS
import { Observable, of, BehaviorSubject } from 'rxjs';
import { mergeMap, map, catchError, finalize } from 'rxjs/operators';
// CRUD
import { QueryParamsModel } from 'src/app/core/base/curd';
import { environment } from 'src/environments/environment';
import { OutOfOfficeHTTPService } from './out_of_office-http/out_of_office-http.services';
import { AuthModel } from 'src/app/features/users/Auth/models/auth.model';

@Injectable()
export class OutOfOfficeServices {
  private isLoadingSubject: BehaviorSubject<boolean>;
  sharedData: string;

  lastFilter$: BehaviorSubject<QueryParamsModel> = new BehaviorSubject(new QueryParamsModel({}, 'asc', '', 0, 10));
  constructor(
    private outOfOfficeHTTPService: OutOfOfficeHTTPService,
  ) { }

  getUserOutbound(token, username) {
    return this.outOfOfficeHTTPService.getUserOutbound(token, username).pipe(
      mergeMap((user) => {
        return of(user);
      })
    );
  }

  AddOOf(name, token, params = {}) {
    return this.outOfOfficeHTTPService.AddOOf(name, token, params).pipe(
      map((res: AuthModel) => {
        return res;
      }),
      catchError((err) => {
        return of(err);
      }),
      finalize(() => this.isLoadingSubject?.next(false))
    );
  }

  deleteOOfO(id, outofofficeid, token) {
    return this.outOfOfficeHTTPService.deleteOOfO(id, outofofficeid, token).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        return of(err);
      }),
    );
  }

  getGroupsById(id, token) {
    return this.outOfOfficeHTTPService.getGroupsById(id, token).pipe(
      mergeMap((res) => {
        return of(res);
      })
    );
  }

  /** v1 parity: OOF UI uses IANA `zone` from `/app/time_zone` for the viewed user. */
  syncSharedDataFromTimeZoneCatalog(userTimeZone: unknown, catalog: unknown): void {
    const iana = resolveUserIanaTimeZone(userTimeZone, catalog);
    if (iana) {
      this.sharedData = iana;
    }
  }

}

export function extractAppTimeZoneItems(catalog: unknown): Array<{ id?: string | number; name?: string; zone?: string }> {
  if (Array.isArray(catalog)) {
    return catalog;
  }
  if (catalog && typeof catalog === 'object') {
    const record = catalog as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      return record.data as Array<{ id?: string | number; name?: string; zone?: string }>;
    }
    if (Array.isArray(record.items)) {
      return record.items as Array<{ id?: string | number; name?: string; zone?: string }>;
    }
    if (Array.isArray(record.time_zone)) {
      return record.time_zone as Array<{ id?: string | number; name?: string; zone?: string }>;
    }
    if (Array.isArray(record.time_zones)) {
      return record.time_zones as Array<{ id?: string | number; name?: string; zone?: string }>;
    }
    if (Array.isArray(record.timeZones)) {
      return record.timeZones as Array<{ id?: string | number; name?: string; zone?: string }>;
    }
  }
  return [];
}

/** Map API user `time_zone` (display name or numeric id) to IANA for dayjs.tz (v1 `tz.zone`). */
export function resolveUserIanaTimeZone(userTimeZone: unknown, catalog: unknown): string {
  const raw = String(userTimeZone ?? '').trim();
  if (!raw) {
    return '';
  }

  const items = extractAppTimeZoneItems(catalog);
  for (const item of items) {
    const record = item as Record<string, unknown>;
    const name = String(item?.name ?? record?.['time_zone_name'] ?? record?.['display_name'] ?? '').trim();
    const id = item?.id ?? record?.['time_zone_id'] ?? record?.['timeZoneId'];
    const zone = String(item?.zone ?? record?.['iana'] ?? record?.['iana_zone'] ?? '').trim();
    if (
      raw === name ||
      raw === zone ||
      (id !== undefined && id !== null && raw === String(id))
    ) {
      return zone || name || raw;
    }
  }

  return raw;
}
